import { Location } from '@angular/common';
import { Component, HostListener, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { MeterStateEnums } from 'src/app/enum/meter-enums';
import { MeterModel } from 'src/app/models/maintenance/meter.model';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { DateConverterService } from 'src/app/services/global-services/date-converter.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { MeterService } from 'src/app/services/maintenance/meter.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-meter',
  templateUrl: './register-meter.component.html',
  styleUrl: './register-meter.component.scss',
  standalone: false
})
export class RegisterMeterComponent {

  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly branchService = inject(BranchService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly dateConverterService = inject(DateConverterService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly headerService = inject(HeaderService);
  private readonly location = inject(Location);
  private readonly meterService = inject(MeterService);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly windowService = inject(WindowService);

  branchId: number = 0;
  companyId: number = 0;
  disabledForm: boolean = true;
  formMeter: FormGroup = new FormGroup({});
  hasQuantityInZero: boolean = false;
  iconState: string = '';
  isOpened: string | undefined = undefined;
  listBranches: any[] = [];
  meterId: number = 0;
  meterModel: MeterModel = new MeterModel();
  nameWindow: string = 'Captura Medidores';
  permissionPrint: boolean = false;
  permissionUpdatable: boolean = false;
  permissionsUser: any[] = [];
  projectName = ProjectName.projectName;
  quantityDetails: number = 0;
  severityState: string = '';
  stateMeter: string = '';
  titleState: string = '';
  totalDays: number = 0;
  url: string = 'meter';

  async ngOnInit() {
    this.validateForm(new MeterModel());
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.activatedRoute.params.subscribe(params => {
      this.meterId = Object.keys(params).length > 0 ? params?.id : null;
    });
    await this.getPermission();
    await this.handleInitialState();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async changeBranch(branchId: number) {
    this.branchId = branchId;
    this.resetContract();
  }

  async changeConsecutive(consecutive: number) {
    this.branchId = this.formMeter.get("branchId")?.value;
    if (!this.branchId) {
      return this.toastHandlerService.showToast(
        'warning',
        'Seleccione una sucursal.'
      );
    }
    if (!consecutive) {
      this.resetContract();
      return;
    }
    try {
      const response = await firstValueFrom(
        this.meterService.getMeterByKeys(this.branchId, consecutive)
      );
      this.meterId = response.meterId;
      this.meterModel = response;
      this.validateForm(this.meterModel);
      this.location.replaceState(`/${this.url}/register/${this.meterId}`);
    } catch (error) {
      console.error('Error al buscar el medidor.', error);
    }
  }

  async changeMeterState() {
    if (!this.meterId) {
      this.toastHandlerService.showToast(
        'warning',
        'Debe crear un medidor.'
      );
      return;
    }
    if (!this.permissionUpdatable) {
      return this.toastHandlerService.showToast(
        'error',
        `No tiene permisos para cambiar el estado del medidor. Comuníquese con el administrador del sistema.`
      );
    }
    if (this.hasQuantityInZero) {
      return this.toastHandlerService.showToast(
        'warning',
        'Todos los artículos deben tener una Cantidad de Captura mayor a 0.'
      );
    }
    try {
      const response = await firstValueFrom(
        this.meterService.changeState(this.meterId)
      );
      const successMessage = response.meterState
        ? 'El medidor fue abierto exitosamente.'
        : 'El medidor fue cerrado exitosamente.';
      this.toastHandlerService.showToast('success', successMessage);
      this.meterModel = response;
      this.validateForm(this.meterModel);
    } catch (error) {
      console.error('Error al cambiar el estado del medidor.', error);
    }
  }

  calculateDays() {
    const start = this.formMeter.value.startDateMeter;
    const end = this.formMeter.value.endDateMeter;
    if (!start || !end) return;
    const diff = new Date(end).getTime() - new Date(start).getTime();
    this.totalDays = Math.floor(diff / (1000 * 60 * 60 * 24)) + 1;
  }

  async createNewRegister() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para crear un medidor. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    this.resetContract();
  }

  async createMeter(modelMeter: MeterModel) {
    try {
      const response = await firstValueFrom(
        this.meterService.create(modelMeter)
      );
      this.toastHandlerService.showToast(
        'success',
        `El medidor fue creado exitosamente.`
      );
      this.meterId = response.meterId;
      this.meterModel = response;
      this.validateForm(this.meterModel);
      this.location.replaceState(`/${this.url}/register/${this.meterId}`);
    } catch (error) {
      console.error('Error al crear el medidor.', error);
    }
  }

  async deleteMeter() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        `El usuario no tiene permisos para eliminar el medidor. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    if (!this.meterId) {
      this.toastHandlerService.showToast(
        'warning',
        'Debe crear un medidor.'
      );
      return;
    }
    if (this.meterModel.meterState === MeterStateEnums.closed) {
      this.toastHandlerService.showToast(
        'warning',
        'No se puede eliminar el medidor mientras está cerrado.'
      );
      return;
    }
    if (this.quantityDetails > 0) {
      this.toastHandlerService.showToast(
        'warning',
        'No se puede eliminar el medidor mientras tenga detalle.'
      );
      return;
    }
    const accepted = await this.confirmDialogService.confirm('¿Realmente desea eliminar el medidor?');
    if (accepted) {
      try {
        await firstValueFrom(this.meterService.delete(this.meterId));
        this.toastHandlerService.showToast(
          'success',
          'El medidor fue eliminado exitosamente.'
        );
        this.resetContract();
      } catch (error) {
        console.error('Error al eliminar el medidor.', error);
      }
    }
  }

  async getAllLists() {
    try {
      const response = await firstValueFrom(
        this.branchService.getAllBranchesForSelectAsync()
      );
      this.listBranches = response;
    } catch (error) {
      console.error('Error obtener las listas.', error);
    }
  }

  async getButtonClick(buttonClick: any) {
    this.branchId = this.formMeter.get("branchId")?.value;
    if (!this.branchId) {
      return this.toastHandlerService.showToast(
        'warning',
        'Seleccione una sucursal.'
      );
    }
    await this.handleMeterNavigation(buttonClick, this.branchId)
  }

  async getFirstBranchByUserAuthenticated() {
    try {
      const response = await firstValueFrom(
        this.branchService.getFirstBranchByUserAuthenticatedAsync()
      );
      this.branchId = response.branchId;
    } catch (error) {
      console.error('Error al obtener la sucursal principal del usuario.', error);
    }
  }

  async getMeterById() {
    try {
      const response = await firstValueFrom(this.meterService.getById(this.meterId));
      this.branchId = response.branchId;
      this.meterModel = response;
      this.validateForm(this.meterModel);
    } catch (error) {
      console.error('Error al obtener el medidor.', error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await Promise.all([
      this.getAllLists(),
      this.getPermissionProfileByUrl()
    ]);
  }

  async getPermissionProfileByUrl() {
    try {
      const response = await firstValueFrom(
        this.windowService.getPermissionProfileByUrl(this.url)
      );
      this.permissionUpdatable = response;
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  async handleInitialState() {
    if (this.meterId) {
      await this.getMeterById();
    } else {
      await this.getFirstBranchByUserAuthenticated();
      this.createNewRegister();
    }
  }

  async handleMeterNavigation(buttonClick: any, branchId: number) {
    const consecutive = this.formMeter.get("consecutive")?.value;
    const actionMap: Record<string, () => Promise<MeterModel>> = {
      first: () =>
        firstValueFrom(
          this.meterService.getFirstMeterByKeys(branchId)
        ),
      previus: () =>
        firstValueFrom(
          this.meterService.getPreviousMeterByKeys(branchId, consecutive)
        ),
      next: () =>
        firstValueFrom(
          this.meterService.getNextMeterByKeys(branchId, consecutive)
        ),
      last: () =>
        firstValueFrom(
          this.meterService.getLastMeterByKeys(branchId)
        ),
    };
    const action = actionMap[buttonClick.target.id];
    if (!action) {
      this.resetContract();
      return;
    }
    try {
      const meter = await action();
      this.meterId = meter.meterId;
      this.meterModel = meter;
      this.validateForm(this.meterModel);
      this.location.replaceState(`/${this.url}/register/${this.meterId}`);
    } catch (error) {
      console.error(`Error al buscar el medidor (${buttonClick.target.id}).`, error);
    }
  }

  async resetContract() {
    this.meterId = 0;
    this.meterModel = new MeterModel();
    this.meterModel.branchId = this.branchId;
    this.validateForm(this.meterModel);
    this.location.replaceState(`/${this.url}/register`);
  }

  async send() {
    this.validateFormControls();
    if (this.formMeter.invalid) {
      this.showValidationMessages();
      return;
    }
    const model = this.formMeter.getRawValue() as MeterModel;
    model.observation = model.observation?.trim() || null;

    if (this.meterId == 0) {
      await this.createMeter(model);
    } else {
      await this.updateMeter(model);
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      branchId: {
        required: 'Debe seleccionar una Sucursal.',
        min: 'Debe seleccionar una Sucursal válida.'
      },
      startDateMeter: {
        required: 'Debe ingresar la Fecha de Inicio del Medidor.'
      },
      endDateMeter: {
        required: 'Debe ingresar la Fecha de Fin del Medidor.'
      },
      observation: {
        maxlength: 'La Observación no puede tener más de 250 caracteres.'
      },
      meterState: {
        required: 'Debe seleccionar un Estado.',
        min: 'Debe seleccionar un Estado válido.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formMeter.get(controlName);
      if (control && control.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  async updateMeter(meter: MeterModel) {
    this.meterModel.startDateMeter = meter.startDateMeter;
    this.meterModel.endDateMeter = meter.endDateMeter;
    this.meterModel.observation = meter.observation;
    this.meterModel.meterState = meter.meterState;
    try {
      const response = await firstValueFrom(
        this.meterService.update(this.meterModel)
      );
      this.toastHandlerService.showToast(
        'success',
        'El medidor fue modificado exitosamente.'
      );
      this.meterModel = response;
      this.validateForm(this.meterModel);
    } catch (error) {
      console.error('Error al modificar el medidor.', error);
    }
  }

  updateFormState() {
    const stateConfig: any = {
      [MeterStateEnums.open]: {
        icon: 'pi pi-lock-open',
        title: 'Cerrar',
        text: 'Abierto',
        isDisabled: false,
        severityState: 'danger',
        enableConsecutive: this.meterId !== 0
      },
      [MeterStateEnums.closed]: {
        icon: 'pi pi-lock',
        title: 'Abrir',
        text: 'Cerrado',
        isDisabled: true,
        severityState: 'success',
        enableConsecutive: true
      }
    };

    const currentState = this.meterModel.meterState;
    const config = stateConfig[currentState];
    if (!config) return;

    this.iconState = config.icon;
    this.titleState = config.title;
    this.severityState = config.severityState;
    this.stateMeter = config.text;
    this.disabledForm = config.isDisabled;

    const form = this.formMeter;

    if (!this.permissionUpdatable || config.isDisabled) {
      form.disable();
    } else {
      form.enable();
    }

    const consecutiveControl = form.controls['consecutive'];
    config.enableConsecutive ? consecutiveControl.enable() : consecutiveControl.disable();

    form.controls['branchId'].enable();
  }

  validateForm(meter: MeterModel) {
    this.formMeter = this.formBuilder.group({
      branchId: [meter.branchId, [Validators.required, Validators.min(1)]],
      consecutive: [meter.consecutive],
      startDateMeter: [meter.startDateMeter
        ? this.dateConverterService.convertUtcToLocalDate(meter.startDateMeter)
        : null, [Validators.required]],
      endDateMeter: [meter.endDateMeter
        ? this.dateConverterService.convertUtcToLocalDate(meter.endDateMeter)
        : null, [Validators.required]],
      observation: [meter.observation, [Validators.maxLength(250)]],
      meterState: [meter.meterState, [Validators.required, Validators.min(1)]],
    });
    this.calculateDays();
    this.updateFormState();
  }

  validateFormControls() {
    Object.keys(this.formMeter.controls).forEach((key) => {
      const control = this.formMeter.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
